import { useEffect, useId, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { useCloseGuard } from "@/hooks/use-close-guard";

/** The console's floor, the same one tenant suspension asks for. */
export const SUSPEND_REASON_MIN_LENGTH = 10;
/** Mirrors SuspendUserCommandValidator. */
export const SUSPEND_REASON_MAX_LENGTH = 500;

interface SuspendUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  /** The suspend request is in flight. */
  isPending: boolean;
  /** Receives the trimmed reason. The caller closes the dialog once the request succeeds. */
  onConfirm: (reason: string) => void;
}

/**
 * Asks for the reason before a user is suspended.
 *
 * The endpoint has always taken one (`SuspendUserRequest.Reason`, carried on `UserSuspendedEvent`),
 * but both places that suspend a user went through a text-only confirmation and sent nothing, so
 * every suspension was recorded without saying why.
 *
 * Held open while the request runs — Cancel, Escape, the corner X and outside clicks are all
 * blocked. On failure it stays open with the reason kept, so the operator can retry without
 * retyping it.
 */
export function SuspendUserDialog({
  open,
  onOpenChange,
  userName,
  isPending,
  onConfirm,
}: SuspendUserDialogProps) {
  const reasonFieldId = useId();
  const [reason, setReason] = useState("");
  /**
   * A suspend request is outstanding. `isPending` is the parent's mutation state and only arrives
   * on the NEXT render, so two clicks in the same tick would otherwise both fire.
   */
  const confirmInFlight = useRef(false);

  // A fresh open starts empty, so one user's reason can never be submitted against another.
  useEffect(() => {
    if (open) {
      setReason("");
      confirmInFlight.current = false;
    }
  }, [open]);

  // This dialog does not own the request, so the parent's mutation settling releases the latch.
  useEffect(() => {
    if (!isPending) {
      confirmInFlight.current = false;
    }
  }, [isPending]);

  const trimmedReason = reason.trim();
  const reasonTooShort = trimmedReason.length < SUSPEND_REASON_MIN_LENGTH;
  const canConfirm = !isPending && !reasonTooShort;

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: trimmedReason.length > 0,
    isSubmitting: isPending,
    onDiscard: () => setReason(""),
  });

  const handleConfirm = () => {
    if (confirmInFlight.current || !canConfirm) {
      return;
    }

    confirmInFlight.current = true;
    onConfirm(trimmedReason);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-2">
            <DialogDescription>
              {`${userName} will lose access to the system until they are activated again.`}
            </DialogDescription>
            <Label htmlFor={reasonFieldId}>Reason for suspending</Label>
            <Textarea
              id={reasonFieldId}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g., Repeated abuse reports on this account"
              maxLength={SUSPEND_REASON_MAX_LENGTH}
              rows={4}
              disabled={isPending}
              aria-invalid={reason.length > 0 && reasonTooShort}
            />
            <p className="text-xs text-muted-foreground">
              {`At least ${SUSPEND_REASON_MIN_LENGTH} characters, at most ${SUSPEND_REASON_MAX_LENGTH}. Recorded with the suspension.`}
            </p>
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              {isPending && <Spinner className="mr-2" />}
              {isPending ? "Suspending..." : "Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
